import { useState } from 'react';
import { useT } from '../../lib/i18n';

// Light-weight collapsible wrapper used inside CapacityTile to fold
// the secondary sections (source mix, earnings outlook) by default.
// Header shows the section title + a "summary" string (whatever the
// caller wants — a headline number, "62% walk-in · Tue heaviest",
// "$58/hr · $2.4k gap", etc.) so the user can see the gist at a
// glance and only expand when they want detail.
//
// Not a generic UI primitive — this is intentionally simple and
// scoped to the Capacity tile's collapse pattern. Three behaviors
// kept narrow on purpose:
//   1. State lives in React (uncontrolled). No persistence across
//      mounts — feels right for "secondary content I want to peek
//      at." Persisting per-section would require a settings entry.
//   2. The summary is plain text passed by the caller. No icons /
//      chips / colors inside the trigger.
//   3. Animation is height auto + chevron rotate — no measured
//      heights or transitions on the body content itself.

export function CollapsibleSection({
  titleKey,
  summary,
  defaultOpen = false,
  children,
}: {
  /** i18n key resolving to the section title shown in the trigger.
   *  Reusing existing dash.capacity.*.title keys where possible
   *  keeps the copy consistent across views. */
  titleKey: string;
  /** One-line headline summary rendered to the right of the title
   *  in the collapsed state. Passed in as a string (already
   *  formatted) so the caller controls all data shaping. */
  summary?: string;
  /** Default-open in rare cases (e.g. when the section IS the
   *  primary content for a given range). Most CapacityTile usage
   *  leaves this false so the user sees a clean tile first. */
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const t = useT();
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-line bg-bg-alt overflow-hidden">
      {/* Trigger row — full-width button so the entire bar is
          tappable, not just the chevron. Title on the left, summary
          + chevron on the right. */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 p-3 text-left hover:bg-bg-alt/70 transition-colors"
      >
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.14em] uppercase font-extrabold text-ink-500">
            {t(titleKey)}
          </div>
          {summary && (
            <div className="text-[12px] text-ink-700 dark:text-ink-300 mt-0.5 truncate">
              {summary}
            </div>
          )}
        </div>
        <ChevronDown
          className={`w-4 h-4 shrink-0 text-ink-500 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>
      {/* Body — only mounted when open. We deliberately UN-MOUNT on
          close so heavy children (forecast hooks, heatmap subgrid)
          don't keep rendering work alive in the background. */}
      {open && (
        <div className="px-3 pb-3 border-t border-line/60">
          {/* Top spacing inside the body matches the trigger's pad
              so the visual rhythm doesn't jump when expanding. */}
          <div className="pt-3">{children}</div>
        </div>
      )}
    </div>
  );
}

function ChevronDown({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
